/**
 * FaqSection — V2 two-column FAQ (approved landing #faq)
 * ──────────────────────────────────────────────────────
 * Renders {ar,en} items from src/seo/content.js. The same data is baked into
 * each route's FAQPage structured data, so what Google reads in the schema is
 * exactly what visitors see here (answers are always in the DOM, native
 * <details>; the first one starts open).
 *
 * Props
 *   faqs       [{ q: {ar,en}, a: {ar,en} }]  (required)
 *   title      heading (default «كل اللي تحتاج تعرفه.» / 'Everything you need to know.')
 *   label      eyebrow (default «أسئلة شائعة» / 'FAQ')
 *   sub        side paragraph (default: the approved landing line; null hides it)
 *   cta        { label, to } side button + phone button under the list
 *              (default «شوف الأسعار» → /pricing; null hides it)
 *   id         id of the block (default 'faq'; scrollToHash lands it under the header)
 *   itemIds    { [index]: id } ids on single questions (e.g. { 2: 'pricing' }); a
 *              /#<id> link opens that question
 *   firstOpen  open the first question initially (default true)
 * Styles: src/v2/landing/landing.css (`.faq`, `.qa-*`, under `.pg-landing`).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft01Icon, ArrowRight01Icon, MinusSignIcon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { useLocale } from '../lib/i18n';
import Icon from './v2/Icon.jsx';

/** Keep «Apple Wallet» / «Google Wallet» on one line without touching the words. */
function noBreakBrands(text) {
  const parts = String(text).split(/(Apple Wallet|Google Wallet)/);
  if (parts.length === 1) return text;
  return parts.map((p, i) => (i % 2 === 1 ? <span key={i} className="nw">{p}</span> : p));
}

export default function FaqSection({ faqs, title, label, sub, cta, id = 'faq', itemIds = {}, firstOpen = true }) {
  const locale = useLocale();
  const ar = locale === 'ar';
  const t = (a, e) => (ar ? a : e);
  const subText = sub === undefined
    ? t('أسئلة نسمعها كثير عن البطاقة الرقمية، والأسعار، واستخدامها في الشركات.', 'Questions we hear a lot about the digital card, pricing, and using it at companies.')
    : sub;
  const ctaItem = cta === undefined ? { label: t('شوف الأسعار', 'See pricing'), to: '/pricing' } : cta;
  const arrow = ar ? ArrowLeft01Icon : ArrowRight01Icon;

  return (
    <section className="sx">
      <div className="wrap faq" id={id || undefined}>
        <div className="h">
          <div className="eb">{label || t('أسئلة شائعة', 'FAQ')}</div>
          <h2>{title || t('كل اللي تحتاج تعرفه.', 'Everything you need to know.')}</h2>
          {subText ? <p>{subText}</p> : null}
          {ctaItem ? <Link className="btn" to={ctaItem.to}>{ctaItem.label}<Icon icon={arrow} /></Link> : null}
        </div>
        <div>
          <div className="qa-l">
            {faqs.map((f, i) => (
              <details className="qa-i" key={i} id={itemIds[i]} open={firstOpen && i === 0 ? true : undefined}>
                <summary>
                  {f.q[locale] ?? f.q.ar}
                  <span className="pm" aria-hidden="true">
                    <Icon icon={PlusSignIcon} className="pl" />
                    <Icon icon={MinusSignIcon} className="mi" />
                  </span>
                </summary>
                <div className="aa">{noBreakBrands(f.a[locale] ?? f.a.ar)}</div>
              </details>
            ))}
          </div>
          {ctaItem ? <Link className="btn lg more" to={ctaItem.to}>{ctaItem.label}<Icon icon={arrow} /></Link> : null}
        </div>
      </div>
    </section>
  );
}
